import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  Target, 
  TrendingUp, 
  Shield, 
  Heart,
  Home,
  GraduationCap,
  Car,
  Plane,
  Building,
  Banknote,
  Star,
  ArrowRight,
  CheckCircle,
  Clock,
  DollarSign,
  Users,
  Calculator
} from 'lucide-react';

interface DataPoint {
  annee: number;
  valeur: number;
  capitalInvesti: number;
  objectifAtteint: number;
}

type ObjectifType = 'retraite' | 'immobilier' | 'education' | 'urgence' | 'voyage' | 'autre';
type HorizonType = 'court' | 'moyen' | 'long';
type SituationType = 'debutant' | 'experimente' | 'expert';

interface Objectif {
  id: ObjectifType;
  nom: string;
  description: string;
  icon: React.ReactNode;
  couleur: string;
  horizonRecommande: HorizonType;
}

interface RecommandationProduit {
  produit: 'SCPI' | 'GFI' | 'Sukuk';
  pourcentage: number;
  couleur: string;
}

interface StrategiePersonnalisee {
  nom: string;
  description: string;
  rendementEstime: number;
  niveauRisque: string;
  allocation: RecommandationProduit[];
  avantages: string[];
  recommandations: string[];
}

const objectifs: Objectif[] = [
  {
    id: 'retraite',
    nom: 'Préparation Retraite',
    description: 'Constituer un capital pour une retraite confortable',
    icon: <Clock className="w-6 h-6" />,
    couleur: 'from-blue-500 to-indigo-600',
    horizonRecommande: 'long'
  },
  {
    id: 'immobilier',
    nom: 'Achat Immobilier',
    description: 'Financer un projet immobilier ou un apport',
    icon: <Home className="w-6 h-6" />,
    couleur: 'from-green-500 to-emerald-600',
    horizonRecommande: 'moyen'
  },
  {
    id: 'education',
    nom: 'Éducation des Enfants',
    description: 'Financer les études supérieures',
    icon: <GraduationCap className="w-6 h-6" />,
    couleur: 'from-purple-500 to-violet-600',
    horizonRecommande: 'moyen'
  },
  {
    id: 'urgence',
    nom: 'Fonds d\'Urgence',
    description: 'Constituer une réserve de sécurité',
    icon: <Shield className="w-6 h-6" />,
    couleur: 'from-amber-500 to-orange-600',
    horizonRecommande: 'court'
  },
  {
    id: 'voyage',
    nom: 'Projet Personnel',
    description: 'Financer un voyage ou un projet de vie',
    icon: <Plane className="w-6 h-6" />,
    couleur: 'from-teal-500 to-cyan-600',
    horizonRecommande: 'court'
  },
  {
    id: 'autre',
    nom: 'Placement Général',
    description: 'Faire fructifier son épargne sans objectif précis',
    icon: <TrendingUp className="w-6 h-6" />,
    couleur: 'from-rose-500 to-pink-600',
    horizonRecommande: 'moyen'
  }
];

const SimulateurBesoins: React.FC = () => {
  // États pour le questionnaire
  const [etapeCourante, setEtapeCourante] = useState<number>(1);
  const [objectifSelectionne, setObjectifSelectionne] = useState<ObjectifType | null>(null);
  const [montantObjectif, setMontantObjectif] = useState<number>(100000);
  const [capitalInitial, setCapitalInitial] = useState<number>(10000);
  const [versementMensuel, setVersementMensuel] = useState<number>(300);
  const [horizonAnnees, setHorizonAnnees] = useState<number>(10);
  const [situationInvestisseur, setSituationInvestisseur] = useState<SituationType>('debutant');
  const [toleranceRisque, setToleranceRisque] = useState<number>(3);
  
  // États pour les résultats
  const [strategieRecommandee, setStrategieRecommandee] = useState<StrategiePersonnalisee | null>(null);
  const [data, setData] = useState<DataPoint[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);

  const objectif = objectifs.find(o => o.id === objectifSelectionne);

  const genererStrategiePersonnalisee = (): StrategiePersonnalisee => {
    let allocation: RecommandationProduit[] = [];
    let rendementEstime = 0.04;
    let niveauRisque = 'Modéré';
    let nom = 'Stratégie Équilibrée';
    let description = '';
    let avantages: string[] = [];
    let recommandations: string[] = [];

    // Logique de recommandation basée sur les réponses
    if (horizonAnnees <= 3) {
      // Court terme - Privilégier la sécurité
      allocation = [
        { produit: 'Sukuk', pourcentage: 70, couleur: '#f59e0b' },
        { produit: 'SCPI', pourcentage: 25, couleur: '#3b82f6' },
        { produit: 'GFI', pourcentage: 5, couleur: '#10b981' }
      ];
      rendementEstime = 0.035;
      niveauRisque = 'Faible';
      nom = 'Stratégie Sécurisée';
      description = 'Priorité à la préservation du capital avec des produits à faible risque';
      avantages = ['Capital préservé', 'Liquidité rapide', 'Revenus stables'];
      recommandations = ['Idéal pour les objectifs à court terme', 'Risque minimal de perte'];
    } else if (horizonAnnees <= 7) {
      // Moyen terme - Équilibre sécurité/rendement
      if (toleranceRisque <= 2) {
        allocation = [
          { produit: 'Sukuk', pourcentage: 50, couleur: '#f59e0b' },
          { produit: 'SCPI', pourcentage: 40, couleur: '#3b82f6' },
          { produit: 'GFI', pourcentage: 10, couleur: '#10b981' }
        ];
        rendementEstime = 0.04;
        nom = 'Stratégie Prudente';
      } else if (toleranceRisque >= 4) {
        allocation = [
          { produit: 'SCPI', pourcentage: 45, couleur: '#3b82f6' },
          { produit: 'GFI', pourcentage: 35, couleur: '#10b981' },
          { produit: 'Sukuk', pourcentage: 20, couleur: '#f59e0b' }
        ];
        rendementEstime = 0.055;
        nom = 'Stratégie Dynamique';
      } else {
        allocation = [
          { produit: 'SCPI', pourcentage: 40, couleur: '#3b82f6' },
          { produit: 'Sukuk', pourcentage: 35, couleur: '#f59e0b' },
          { produit: 'GFI', pourcentage: 25, couleur: '#10b981' }
        ];
        rendementEstime = 0.045;
        nom = 'Stratégie Équilibrée';
      }
      description = 'Combinaison optimale entre sécurité et performance pour le moyen terme';
      avantages = ['Croissance modérée', 'Risque maîtrisé', 'Diversification'];
      recommandations = ['Parfait pour projets à moyen terme', 'Bon équilibre risque/rendement'];
    } else {
      // Long terme - Privilégier la croissance
      if (toleranceRisque <= 2) {
        allocation = [
          { produit: 'SCPI', pourcentage: 50, couleur: '#3b82f6' },
          { produit: 'Sukuk', pourcentage: 30, couleur: '#f59e0b' },
          { produit: 'GFI', pourcentage: 20, couleur: '#10b981' }
        ];
        rendementEstime = 0.045;
        nom = 'Stratégie Long Terme Prudente';
      } else {
        allocation = [
          { produit: 'GFI', pourcentage: 55, couleur: '#10b981' },
          { produit: 'SCPI', pourcentage: 35, couleur: '#3b82f6' },
          { produit: 'Sukuk', pourcentage: 10, couleur: '#f59e0b' }
        ];
        rendementEstime = 0.065;
        niveauRisque = 'Élevé';
        nom = 'Stratégie de Croissance';
      }
      description = 'Maximisation du potentiel de croissance sur le long terme';
      avantages = ['Potentiel élevé', 'Effet de levier temporel', 'Diversification internationale'];
      recommandations = ['Idéal pour la retraite', 'Laisser le temps faire son œuvre'];
    }

    // Ajustements selon l'objectif
    if (objectifSelectionne === 'retraite') {
      recommandations.push('Versements programmés recommandés', 'Réinvestissement des gains');
    } else if (objectifSelectionne === 'immobilier') {
      recommandations.push('Prévoir la liquidation progressive', 'Surveiller les taux immobiliers');
    } else if (objectifSelectionne === 'urgence') {
      recommandations.push('Privilégier la liquidité', 'Éviter les blocages longs');
    }

    return {
      nom,
      description,
      rendementEstime,
      niveauRisque,
      allocation,
      avantages,
      recommandations
    };
  };

  const calculerProjection = () => {
    if (!strategieRecommandee) return;

    const nouvelleDonnee: DataPoint[] = [];
    const tauxRendement = strategieRecommandee.rendementEstime;
    
    for (let annee = 0; annee <= horizonAnnees; annee++) {
      const capitalInvesti = capitalInitial + (versementMensuel * 12 * annee);
      
      let valeur = capitalInitial;
      for (let a = 1; a <= annee; a++) {
        valeur = valeur * (1 + tauxRendement) + (versementMensuel * 12);
      }
      
      nouvelleDonnee.push({
        annee,
        valeur: Math.round(valeur),
        capitalInvesti: Math.round(capitalInvesti),
        objectifAtteint: montantObjectif
      });
    }
    
    setData(nouvelleDonnee);
  };

  const genererRecommandation = () => {
    const strategie = genererStrategiePersonnalisee();
    setStrategieRecommandee(strategie);
    setShowResults(true);
    setTimeout(() => {
      calculerProjection();
    }, 100);
  };

  useEffect(() => {
    if (strategieRecommandee && showResults) {
      calculerProjection();
    }
  }, [strategieRecommandee, capitalInitial, versementMensuel, horizonAnnees, montantObjectif]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const valeurFinale = data.length > 0 ? data[data.length - 1].valeur : 0;
  const objectifAtteint = valeurFinale >= montantObjectif;
  const pourcentageObjectif = Math.round((valeurFinale / montantObjectif) * 100);

  if (showResults && strategieRecommandee) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ✨ Votre Stratégie Personnalisée
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {strategieRecommandee.nom}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {strategieRecommandee.description}
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8">
            {/* Résumé de l'objectif */}
            <div className="xl:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                <div className="flex items-center space-x-3 mb-6">
                  <div className={`w-12 h-12 bg-gradient-to-br ${objectif?.couleur} rounded-xl flex items-center justify-center text-white`}>
                    {objectif?.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{objectif?.nom}</h3>
                    <p className="text-sm text-gray-600">Votre objectif principal</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Objectif</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(montantObjectif)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Horizon</span>
                    <span className="text-lg font-bold text-gray-900">{horizonAnnees} ans</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Capital initial</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(capitalInitial)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Versement mensuel</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(versementMensuel)}</span>
                  </div>
                </div>
              </div>

              {/* Allocation recommandée */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Répartition Recommandée</h3>
                <div className="h-64 mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={strategieRecommandee.allocation}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        dataKey="pourcentage"
                        nameKey="produit"
                      >
                        {strategieRecommandee.allocation.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.couleur} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value: any) => [`${value}%`, 'Allocation']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {strategieRecommandee.allocation.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: item.couleur }}
                        />
                        <span className="text-sm font-medium">{item.produit}</span>
                      </div>
                      <span className="text-sm font-bold">{item.pourcentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Résultats et graphique */}
            <div className="xl:col-span-2 space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className={`bg-white rounded-2xl p-6 shadow-xl border border-gray-100 ${objectifAtteint ? 'ring-2 ring-green-500' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center">
                      {objectifAtteint ? <CheckCircle className="w-6 h-6 text-white" /> : <Target className="w-6 h-6 text-white" />}
                    </div>
                  </div>
                  <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Objectif</h3>
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    {pourcentageObjectif}%
                  </p>
                  <div className={`text-xs font-medium px-2 py-1 rounded-full inline-block ${objectifAtteint ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                    {objectifAtteint ? 'Objectif atteint' : 'En cours'}
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Valeur Finale</h3>
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    {formatCurrency(valeurFinale)}
                  </p>
                  <div className="text-xs text-green-600 font-bold bg-green-50 px-2 py-1 rounded-full inline-block">
                    +{formatCurrency(valeurFinale - (capitalInitial + versementMensuel * 12 * horizonAnnees))} gain
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-violet-600 rounded-xl flex items-center justify-center">
                      <Calculator className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Rendement</h3>
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    {(strategieRecommandee.rendementEstime * 100).toFixed(1)}%
                  </p>
                  <div className="text-xs text-purple-600 font-medium bg-purple-50 px-2 py-1 rounded-full inline-block">
                    {strategieRecommandee.niveauRisque}
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Total Investi</h3>
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    {formatCurrency(capitalInitial + versementMensuel * 12 * horizonAnnees)}
                  </p>
                  <div className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded-full inline-block">
                    Capital + versements
                  </div>
                </div>
              </div>

              {/* Graphique */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Évolution de Votre Investissement</h3>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="annee" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '0.5rem',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value: any, name: string) => [
                          formatCurrency(value),
                          name === 'valeur' ? 'Valeur du portefeuille' : 
                          name === 'capitalInvesti' ? 'Capital investi' : 'Objectif'
                        ]}
                        labelFormatter={(label) => `Année ${label}`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="valeur"
                        stroke="#8b5cf6"
                        strokeWidth={3}
                        name="Valeur du portefeuille"
                        dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="capitalInvesti"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Capital investi"
                        dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="objectifAtteint"
                        stroke="#10b981"
                        strokeWidth={2}
                        strokeDasharray="10 5"
                        name="Objectif"
                        dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Recommandations détaillées */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <CheckCircle className="w-6 h-6 text-green-500 mr-2" />
                Avantages de Cette Stratégie
              </h3>
              <ul className="space-y-3">
                {strategieRecommandee.avantages.map((avantage, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">{avantage}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Target className="w-6 h-6 text-blue-500 mr-2" />
                Recommandations Personnalisées
              </h3>
              <ul className="space-y-3">
                {strategieRecommandee.recommandations.map((recommandation, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700">{recommandation}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Action finale */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-12 text-white">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold mb-4">
                  Prêt à Concrétiser Votre Stratégie ?
                </h2>
                <p className="text-xl mb-8 opacity-90">
                  Cette stratégie personnalisée a été conçue spécifiquement pour vos besoins et votre profil d'investisseur.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => setShowResults(false)}
                    className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-gray-100 transition-all duration-300"
                  >
                    Modifier mes Critères
                  </button>
                  <button className="bg-white bg-opacity-20 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-opacity-30 transition-all duration-300 border-2 border-white border-opacity-30">
                    Commencer cet Investissement
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Interface du questionnaire
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Analyse Personnalisée
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Trouvez Votre Stratégie Idéale
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Répondez à quelques questions pour obtenir une recommandation d'investissement sur mesure
          </p>
        </div>

        {/* Indicateur de progression */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Progression</span>
            <span className="text-sm font-medium text-gray-600">{etapeCourante}/4</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(etapeCourante / 4) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Étape 1: Objectif d'investissement */}
        {etapeCourante === 1 && (
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Quel est votre objectif principal ?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {objectifs.map((obj) => (
                <div
                  key={obj.id}
                  className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                    objectifSelectionne === obj.id
                      ? `bg-gradient-to-br ${obj.couleur} text-white border-transparent`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setObjectifSelectionne(obj.id)}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      objectifSelectionne === obj.id ? 'bg-white bg-opacity-20' : 'bg-gray-100'
                    }`}>
                      <div className={objectifSelectionne === obj.id ? 'text-white' : 'text-gray-600'}>
                        {obj.icon}
                      </div>
                    </div>
                    <h3 className="text-lg font-bold">{obj.nom}</h3>
                  </div>
                  <p className={`text-sm ${objectifSelectionne === obj.id ? 'text-white opacity-90' : 'text-gray-600'}`}>
                    {obj.description}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-8">
              <button
                onClick={() => objectifSelectionne && setEtapeCourante(2)}
                disabled={!objectifSelectionne}
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:from-purple-600 hover:to-blue-600 transition-all duration-300 flex items-center space-x-2"
              >
                <span>Continuer</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Étape 2: Montant et horizon */}
        {etapeCourante === 2 && (
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Définissez votre objectif financier
            </h2>
            <div className="space-y-8">
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-4">
                  Montant de votre objectif
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="10000"
                    max="1000000"
                    step="10000"
                    value={montantObjectif}
                    onChange={(e) => setMontantObjectif(Number(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-2xl font-bold text-purple-600 min-w-[120px] text-right">
                    {formatCurrency(montantObjectif)}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-4">
                  Dans combien d'années ?
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={horizonAnnees}
                    onChange={(e) => setHorizonAnnees(Number(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-2xl font-bold text-blue-600 min-w-[100px] text-right">
                    {horizonAnnees} ans
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-4">
                  Capital de départ disponible
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="0"
                    max="500000"
                    step="1000"
                    value={capitalInitial}
                    onChange={(e) => setCapitalInitial(Number(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-2xl font-bold text-green-600 min-w-[120px] text-right">
                    {formatCurrency(capitalInitial)}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-4">
                  Versement mensuel possible
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="0"
                    max="2000"
                    step="50"
                    value={versementMensuel}
                    onChange={(e) => setVersementMensuel(Number(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-2xl font-bold text-orange-600 min-w-[120px] text-right">
                    {formatCurrency(versementMensuel)}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between mt-8">
              <button
                onClick={() => setEtapeCourante(1)}
                className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-300"
              >
                Retour
              </button>
              <button
                onClick={() => setEtapeCourante(3)}
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-blue-600 transition-all duration-300 flex items-center space-x-2"
              >
                <span>Continuer</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Étape 3: Profil investisseur */}
        {etapeCourante === 3 && (
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Parlez-nous de votre profil
            </h2>
            <div className="space-y-8">
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-4">
                  Votre expérience en investissement
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { id: 'debutant', nom: 'Débutant', description: 'Peu ou pas d\'expérience' },
                    { id: 'experimente', nom: 'Expérimenté', description: 'Quelques années d\'expérience' },
                    { id: 'expert', nom: 'Expert', description: 'Très expérimenté' }
                  ].map((niveau) => (
                    <div
                      key={niveau.id}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                        situationInvestisseur === niveau.id
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSituationInvestisseur(niveau.id as SituationType)}
                    >
                      <h3 className="font-bold">{niveau.nom}</h3>
                      <p className={`text-sm ${situationInvestisseur === niveau.id ? 'text-white opacity-90' : 'text-gray-600'}`}>
                        {niveau.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-4">
                  Votre tolérance au risque (1 = Très prudent, 5 = Très audacieux)
                </label>
                <div className="flex items-center justify-center space-x-4 py-4">
                  {[1, 2, 3, 4, 5].map((niveau) => (
                    <button
                      key={niveau}
                      onClick={() => setToleranceRisque(niveau)}
                      className={`w-12 h-12 rounded-full border-2 font-bold text-lg transition-all duration-300 ${
                        toleranceRisque === niveau
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {niveau}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-sm text-gray-600 mt-2">
                  <span>Très prudent</span>
                  <span>Très audacieux</span>
                </div>
              </div>
            </div>
            <div className="flex justify-between mt-8">
              <button
                onClick={() => setEtapeCourante(2)}
                className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-300"
              >
                Retour
              </button>
              <button
                onClick={() => setEtapeCourante(4)}
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-blue-600 transition-all duration-300 flex items-center space-x-2"
              >
                <span>Voir ma stratégie</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Étape 4: Récapitulatif avant génération */}
        {etapeCourante === 4 && (
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Récapitulatif de vos informations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 bg-gradient-to-br ${objectif?.couleur} rounded-lg flex items-center justify-center text-white`}>
                    {objectif?.icon}
                  </div>
                  <div>
                    <div className="font-semibold">{objectif?.nom}</div>
                    <div className="text-sm text-gray-600">{formatCurrency(montantObjectif)} en {horizonAnnees} ans</div>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Capital initial:</span>
                  <span className="font-semibold">{formatCurrency(capitalInitial)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Versement mensuel:</span>
                  <span className="font-semibold">{formatCurrency(versementMensuel)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expérience:</span>
                  <span className="font-semibold capitalize">{situationInvestisseur}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tolérance au risque:</span>
                  <span className="font-semibold">{toleranceRisque}/5</span>
                </div>
              </div>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setEtapeCourante(3)}
                className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-300"
              >
                Retour
              </button>
              <button
                onClick={genererRecommandation}
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-blue-600 transition-all duration-300 flex items-center space-x-2"
              >
                <span>Générer ma stratégie</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimulateurBesoins;